import { useCallback, useRef, useState } from 'react';
import { streamAiMessage, type SendAiMessageInput, type TurnRetryRefusalCode } from '../api/messages';

const REFUSAL_CODES: readonly string[] = ['TURN_STILL_RUNNING', 'TURN_COMPLETED', 'TURN_ALREADY_RETRIED'];
const asRefusalCode = (code: unknown): TurnRetryRefusalCode | null =>
  typeof code === 'string' && REFUSAL_CODES.includes(code) ? (code as TurnRetryRefusalCode) : null;

/** @deprecated Legacy sub-project #1 shape — kept for backwards compat; always [] in Phase 4a. */
export interface DraftRef {
  pluginId: string;
  entityType: string;
  entityId: string;
}

export interface UndoBannerEntry {
  draftId: string;
  bundleId: string;
  deadlineAt: Date;
  label: string;
}

export interface ChatStreamState {
  isStreaming: boolean;
  currentText: string;
  toolEvents: Array<{ id: string; name: string; uiLabel?: string; done: boolean }>;
  /** @deprecated Always [] in Phase 4a — use bundleIds instead. */
  draftRefs: DraftRef[];
  /** BundleIds produced in the current turn (Phase 4a). */
  bundleIds: string[];
  /** Undo banners for auto-applied drafts (Phase 4a). */
  undoBanners: UndoBannerEntry[];
  error: string | null;
}

export interface ChatStreamFinal {
  finalText: string;
  finalTraces: Array<{ id: string; name: string; uiLabel?: string; done: boolean }>;
  /** @deprecated Always [] in Phase 4a. */
  finalDraftRefs: DraftRef[];
  persisted: { userMessageId: string; assistantMessageId: string } | null;
  error: string | null;
  /**
   * The turn started but its stream ended without a final `done`/`error` —
   * a dropped connection or a draining replica's `reconnect`. Not an error:
   * the server marked the turn interrupted and it can be retried.
   */
  interrupted: boolean;
  /** The turn to pass as `retryOfTurnId`: set when it was interrupted or failed with an error event. */
  retryTurnId: string | null;
  /**
   * A retry (`retryOfTurnId`) the server refused, and why. Not an error: the
   * caller must not ask the question again. A 409 without a known code is
   * reported as TURN_STILL_RUNNING — the one answer that never re-asks.
   */
  retryRefused: TurnRetryRefusalCode | null;
}

const initial: ChatStreamState = {
  isStreaming: false,
  currentText: '',
  toolEvents: [],
  draftRefs: [],
  bundleIds: [],
  undoBanners: [],
  error: null,
};

export const useChatStream = () => {
  const [state, setState] = useState<ChatStreamState>(initial);
  const abortRef = useRef<AbortController | null>(null);

  // `send` returns the terminal state directly — no need for callers to read
  // hook state after await (which is racy across render boundaries). State
  // still updates during streaming so a streaming line can render live.
  const send = useCallback(async (input: SendAiMessageInput): Promise<ChatStreamFinal> => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setState({ ...initial, isStreaming: true });

    // Local accumulators — authoritative for the return value. Hook state
    // mirrors them for UI rendering during the stream.
    let text = '';
    const traces: Array<{ id: string; name: string; uiLabel?: string; done: boolean }> = [];
    let persisted: { userMessageId: string; assistantMessageId: string } | null = null;
    let error: string | null = null;
    let turnId: string | null = null;
    let ended = false; // a final `done` or `error` event arrived
    let failed = false;
    let retryRefused: TurnRetryRefusalCode | null = null;

    try {
      for await (const ev of streamAiMessage(input, ac.signal)) {
        if (ev.type === 'turn_started') {
          turnId = ev.data.turnId;
        } else if (ev.type === 'reconnect') {
          break;
        } else if (ev.type === 'token') {
          text += ev.data.text;
          setState((s) => ({ ...s, currentText: text }));
        } else if (ev.type === 'tool_use_started') {
          traces.push({ id: ev.data.id, name: ev.data.name, uiLabel: ev.data.uiLabel, done: false });
          setState((s) => ({ ...s, toolEvents: [...traces] }));
        } else if (ev.type === 'tool_use_result') {
          const traceId = (ev.data as { trace?: { id?: string } }).trace?.id;
          if (traceId) {
            const idx = traces.findIndex((t) => t.id === traceId);
            if (idx >= 0) traces[idx] = { ...traces[idx]!, done: true };
            setState((s) => ({ ...s, toolEvents: [...traces] }));
          }
        } else if (ev.type === 'draft_created') {
          // Phase 4a: track the bundleId so the message render can show <DraftBundleCard>
          const { bundleId } = ev.data;
          setState((s) => ({
            ...s,
            bundleIds: s.bundleIds.includes(bundleId) ? s.bundleIds : [...s.bundleIds, bundleId],
          }));
        } else if (ev.type === 'draft_auto_applied') {
          // Spawn an undo banner above chat input
          const { draftId, bundleId, undoDeadlineAt } = ev.data;
          setState((s) => ({
            ...s,
            undoBanners: [
              ...s.undoBanners,
              { draftId, bundleId, deadlineAt: new Date(undoDeadlineAt), label: 'Applied a change' },
            ],
          }));
        } else if (ev.type === 'bundle_ready') {
          // Bundle finalized; ensure it's in the list
          const { bundleId } = ev.data;
          setState((s) => ({
            ...s,
            bundleIds: s.bundleIds.includes(bundleId) ? s.bundleIds : [...s.bundleIds, bundleId],
          }));
        } else if (ev.type === 'bundle_available') {
          // Part B: a reviewable bundle exists even though the happy-path
          // draft_created/bundle_ready events didn't fire (dedup re-run, Phase C
          // failure, or a create that couldn't be staged). Always surface the
          // card; on create_failed also raise the actionable message.
          const { bundleId, reason, message } = ev.data;
          if (reason === 'create_failed') {
            error = message ?? error;
          }
          setState((s) => ({
            ...s,
            bundleIds:
              bundleId && !s.bundleIds.includes(bundleId)
                ? [...s.bundleIds, bundleId]
                : s.bundleIds,
            error: reason === 'create_failed' ? (message ?? s.error) : s.error,
          }));
        } else if (ev.type === 'bundle_rejected_conflict') {
          // Surface a toast / log; for now, silently drop
        } else if (ev.type === 'writes_disabled') {
          // Surface a toast / banner; for now, silently drop
        } else if (ev.type === 'error' && input.retryOfTurnId && asRefusalCode(ev.data.code)) {
          // A concurrent retry claimed the turn after the pre-check passed.
          ended = true;
          retryRefused = asRefusalCode(ev.data.code);
        } else if (ev.type === 'error') {
          ended = true;
          failed = true;
          error = ev.data.message;
          setState((s) => ({ ...s, error }));
        } else if (ev.type === 'message_persisted') {
          persisted = ev.data;
        } else if (ev.type === 'done') {
          ended = true;
          break;
        }
      }
    } catch (err) {
      // A refused retry is reported as such, never as an error (whose "Try
      // again" would ask the question afresh). A started turn whose
      // connection drops is interrupted, not failed.
      const status = (err as { status?: unknown }).status;
      if (input.retryOfTurnId && status === 409) {
        retryRefused = asRefusalCode((err as { code?: unknown }).code) ?? 'TURN_STILL_RUNNING';
      } else if ((err as { name?: string }).name !== 'AbortError' && !(turnId && !ended)) {
        error = err instanceof Error ? err.message : 'Stream failed';
        setState((s) => ({ ...s, error }));
      }
    } finally {
      setState((s) => ({ ...s, isStreaming: false }));
    }

    const interrupted = turnId !== null && !ended && !ac.signal.aborted;
    return {
      finalText: text,
      finalTraces: traces,
      finalDraftRefs: [],
      persisted,
      error,
      interrupted,
      retryTurnId: interrupted || failed ? turnId : null,
      retryRefused,
    };
  }, []);

  const cancel = useCallback(() => abortRef.current?.abort(), []);
  const reset = useCallback(() => setState(initial), []);

  const dismissUndoBanner = useCallback((draftId: string) => {
    setState((s) => ({
      ...s,
      undoBanners: s.undoBanners.filter((b) => b.draftId !== draftId),
    }));
  }, []);

  return { ...state, send, cancel, reset, dismissUndoBanner };
};
